// src/pages/TrainGym.js - СО DRAG AND DROP ФУНКЦИОНАЛНОСТ
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Chip,
    Avatar,
    Snackbar
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    SportsMartialArts,
    AccessTime,
    AttachMoney,
    Description,
    KeyboardArrowLeft,
    FitnessCenter,
    DragIndicator // 👈 ДОДАДЕНО ЗА DRAG HANDLE
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AdminHeader from '../components/AdminHeader';
import { gymTrainingAPI } from '../services/api';

const TrainGym = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();

    const [trainings, setTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTraining, setEditingTraining] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, training: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [draggedIndex, setDraggedIndex] = useState(null); // 👈 НОВО ЗА DRAG STATE

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        durationDays: ''
    });
    const [formErrors, setFormErrors] = useState({});

    // Redirect ако не е admin
    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [authLoading, isAuthenticated, user, navigate]);

    // Fetch тренинзи - ВИСТИНСКИ API
    const fetchTrainings = async () => {
        try {
            setLoading(true);
            const data = await gymTrainingAPI.admin.getAll();

            // Сортирај ги по displayOrder или id ако нема displayOrder
            const sortedTrainings = data.sort((a, b) => {
                // Ако имаш displayOrder поле во backend, користи го тоа
                if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
                    return a.displayOrder - b.displayOrder;
                }
                // Инаку сортирај по id
                return a.id - b.id;
            });

            setTrainings(sortedTrainings);
        } catch (error) {
            console.error('Error fetching trainings:', error);
            showSnackbar('Грешка при вчитување на тренинзи', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated && user?.role === 'ADMIN') {
            fetchTrainings();
        }
    }, [authLoading, isAuthenticated, user]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // 👈 НОВИ DRAG AND DROP ФУНКЦИИ
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, targetIndex) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === targetIndex) {
            return;
        }

        const newTrainings = [...trainings];
        const draggedTraining = newTrainings[draggedIndex];

        // Отстрани го повлечениот тренинг од тековната позиција
        newTrainings.splice(draggedIndex, 1);

        // Вметни го на новата позиција
        newTrainings.splice(targetIndex, 0, draggedTraining);

        // Ажурирај го локалниот state веднаш за подобра UX
        setTrainings(newTrainings);

        try {
            // Прати го новиот редослед на backend
            await saveNewOrder(newTrainings);
            showSnackbar('Редоследот е успешно ажуриран! 🔄', 'success');
        } catch (error) {
            console.error('Error updating order:', error);
            showSnackbar('Грешка при ажурирање на редоследот', 'error');
            // Врати го назад стариот редослед ако има грешка
            fetchTrainings();
        }
    };

    // Функција за зачувување на новиот редослед
    const saveNewOrder = async (orderedTrainings) => {
        // Опција 1: Ако backend има endpoint за batch reorder
        try {
            const orderedIds = orderedTrainings.map(training => training.id);
            await gymTrainingAPI.admin.reorder(orderedIds);
        } catch (error) {
            // Опција 2: Ако нема batch endpoint, ажурирај ги индивидуално
            console.log('Batch reorder not available, updating individually...');

            const updatePromises = orderedTrainings.map((training, index) => {
                return gymTrainingAPI.admin.update(training.id, {
                    ...training,
                    displayOrder: index + 1
                });
            });

            await Promise.all(updatePromises);
        }
    };

    // Отвори форма за додавање/едитирање
    const handleOpenDialog = (training = null) => {
        if (training) {
            setEditingTraining(training);
            setFormData({
                name: training.name,
                description: training.description,
                price: training.price.toString(),
                durationDays: training.durationDays.toString()
            });
        } else {
            setEditingTraining(null);
            setFormData({
                name: '',
                description: '',
                price: '',
                durationDays: ''
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    // Затвори дијалог
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTraining(null);
        setFormData({
            name: '',
            description: '',
            price: '',
            durationDays: ''
        });
        setFormErrors({});
    };

    // Валидација на форма
    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Името е задолжително';
        }

        if (!formData.description.trim()) {
            errors.description = 'Описот е задолжителен';
        }

        if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
            errors.price = 'Цената мора да биде валиден позитивен број';
        }

        if (!formData.durationDays || isNaN(formData.durationDays) || parseInt(formData.durationDays) <= 0) {
            errors.durationDays = 'Времетраењето во денови мора да биде валиден позитивен број';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Зачувај тренинг - ВИСТИНСКИ API
    const handleSaveTraining = async () => {
        if (!validateForm()) return;

        try {
            const trainingData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                price: parseFloat(formData.price),
                durationDays: parseInt(formData.durationDays)
            };

            if (editingTraining) {
                await gymTrainingAPI.admin.update(editingTraining.id, trainingData);
                showSnackbar('Тренингот е успешно ажуриран! 🎉');
            } else {
                await gymTrainingAPI.admin.create(trainingData);
                showSnackbar('Тренингот е успешно креиран! 🎉');
            }

            handleCloseDialog();
            fetchTrainings();
        } catch (error) {
            console.error('Error saving training:', error);
            showSnackbar('Грешка при зачувување на тренингот', 'error');
        }
    };

    // Избриши тренинг - ВИСТИНСКИ API
    const handleDeleteTraining = async () => {
        if (!deleteConfirm.training) return;

        try {
            await gymTrainingAPI.admin.delete(deleteConfirm.training.id);
            showSnackbar('Тренингот е успешно избришан! 🗑️');
            setDeleteConfirm({ open: false, training: null });
            fetchTrainings();
        } catch (error) {
            console.error('Error deleting training:', error);
            showSnackbar('Грешка при бришење на тренингот', 'error');
        }
    };

    // Loading state
    if (authLoading || loading) {
        return (
            <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: '#fafafa' }}>
                <Container maxWidth="lg">
                    <AdminHeader />
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography variant="h6">Вчитување...</Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (!isAuthenticated || user?.role !== 'ADMIN') {
        return null;
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: '#fafafa' }}>
            <Container maxWidth="lg">
                <AdminHeader />

                {/* Назад копче */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin')}
                        startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                        variant="outlined"
                        sx={{
                            color: '#AB47BC',
                            borderColor: '#AB47BC',
                            backgroundColor: 'rgba(171, 71, 188, 0.08)',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            px: 3,
                            py: 1.5,
                            borderRadius: '12px',
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(171, 71, 188, 0.2)',
                            border: '2px solid #AB47BC',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#AB47BC',
                                color: 'white',
                                borderColor: '#9C27B0',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(171, 71, 188, 0.4)',
                            }
                        }}
                    >
                        Назад кон Dashboard
                    </Button>
                </Box>

                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(171, 71, 188, 0.1) 0%, rgba(171, 71, 188, 0.05) 100%)',
                        border: '2px solid rgba(171, 71, 188, 0.2)'
                    }}>
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '14px',
                            backgroundColor: '#AB47BC',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px',
                            boxShadow: '0 4px 12px rgba(171, 71, 188, 0.3)'
                        }}>
                            🏋️‍♂️
                        </Box>
                        <Box>
                            <Typography variant="h3" sx={{ color: '#AB47BC', fontWeight: 'bold', lineHeight: 1.1 }}>
                                Тренинг во теретана
                            </Typography>
                            <Typography variant="h6" color="textSecondary" sx={{ mt: 0.5 }}>
                                Управувај со индивидуални тренинзи и сесии
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} mt={1}>
                                <Chip
                                    label={`${trainings.length} тренинзи`}
                                    sx={{ bgcolor: '#AB47BC', color: 'white', fontWeight: 'bold' }}
                                />
                                {trainings.length > 1 && (
                                    <Chip
                                        label="🔄 Влечи и пушти за редослед"
                                        variant="outlined"
                                        size="small"
                                        sx={{ borderColor: '#AB47BC', color: '#AB47BC' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                            bgcolor: '#AB47BC',
                            px: 4,
                            py: 1.5,
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: '0 4px 15px rgba(171, 71, 188, 0.3)',
                            '&:hover': {
                                bgcolor: '#9C27B0',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 25px rgba(171, 71, 188, 0.4)',
                            }
                        }}
                    >
                        Додади Тренинг
                    </Button>
                </Box>

                {/* Тренинзи листа */}
                {trainings.length === 0 ? (
                    <Card sx={{
                        borderRadius: 3,
                        border: '2px dashed #AB47BC',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 30% 70%, rgba(171, 71, 188, 0.1) 0%, transparent 50%)',
                            zIndex: 1
                        }} />

                        <CardContent sx={{ textAlign: 'center', py: 8, position: 'relative', zIndex: 2 }}>
                            <FitnessCenter sx={{ fontSize: 100, color: '#AB47BC', mb: 3 }} />
                            <Typography variant="h3" color="#AB47BC" gutterBottom fontWeight="bold">
                                Нема тренинзи
                            </Typography>
                            <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                                Започнете со додавање на првиот тренинг во теретана
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Add />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    bgcolor: '#AB47BC',
                                    px: 5,
                                    py: 2,
                                    borderRadius: 3,
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    boxShadow: '0 6px 20px rgba(171, 71, 188, 0.3)',
                                    '&:hover': {
                                        bgcolor: '#9C27B0',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 30px rgba(171, 71, 188, 0.4)',
                                    }
                                }}
                            >
                                Додади Прв Тренинг
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {trainings.map((training, index) => (
                            <Grid xs={12} md={6} lg={4} key={training.id}>
                                <Card
                                    draggable // 👈 НАПРАВИ ГА DRAGGABLE
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, index)}
                                    sx={{
                                        borderRadius: 3,
                                        border: '2px solid rgba(171, 71, 188, 0.2)',
                                        cursor: 'grab', // 👈 CURSOR INDICATION
                                        transition: 'all 0.3s ease',
                                        opacity: draggedIndex === index ? 0.5 : 1, // 👈 VISUAL FEEDBACK
                                        transform: draggedIndex === index ? 'rotate(5deg)' : 'none',
                                        '&:hover': {
                                            boxShadow: '0 8px 30px rgba(171, 71, 188, 0.3)',
                                            transform: draggedIndex === index ? 'rotate(5deg)' : 'translateY(-4px)',
                                            borderColor: '#AB47BC'
                                        },
                                        '&:active': {
                                            cursor: 'grabbing' // 👈 CURSOR WHILE DRAGGING
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                {/* 👈 DRAG HANDLE */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    color: '#AB47BC',
                                                    cursor: 'grab'
                                                }}>
                                                    <DragIndicator sx={{ fontSize: 20 }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                        #{index + 1}
                                                    </Typography>
                                                </Box>

                                                <Avatar sx={{ bgcolor: '#AB47BC', width: 48, height: 48 }}>
                                                    <SportsMartialArts />
                                                </Avatar>
                                            </Box>

                                            <Box display="flex" gap={1}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDialog(training)}
                                                    sx={{ color: '#AB47BC' }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteConfirm({ open: true, training })}
                                                    sx={{ color: '#f44336' }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#AB47BC', mb: 1 }}>
                                            {training.name}
                                        </Typography>

                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
                                            {training.description}
                                        </Typography>

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                                    {training.price} ден
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AccessTime sx={{ color: '#FF9800', fontSize: 20 }} />
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                                                    {training.durationDays} денови
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Останатите дијалози остануваат исти... */}
                {/* Форма дијалог */}
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ bgcolor: '#AB47BC', color: 'white', py: 3 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <SportsMartialArts />
                            <Typography variant="h5">
                                {editingTraining ? 'Уреди Тренинг' : 'Додади Нов Тренинг'}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ mt: 3 }}>
                        <Grid container spacing={3}>
                            <Grid xs={12}>
                                <TextField
                                    fullWidth
                                    label="Име на тренинг *"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    error={!!formErrors.name}
                                    helperText={formErrors.name}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <TextField
                                    fullWidth
                                    label="Опис *"
                                    multiline
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    error={!!formErrors.description}
                                    helperText={formErrors.description}
                                />
                            </Grid>
                            <Grid xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Цена (денари) *"
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    error={!!formErrors.price}
                                    helperText={formErrors.price}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Времетраење (денови) *"
                                    type="number"
                                    value={formData.durationDays}
                                    onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                                    error={!!formErrors.durationDays}
                                    helperText={formErrors.durationDays}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 2 }}>
                        <Button onClick={handleCloseDialog} variant="outlined" size="large">
                            Откажи
                        </Button>
                        <Button
                            onClick={handleSaveTraining}
                            variant="contained"
                            size="large"
                            sx={{ bgcolor: '#AB47BC', '&:hover': { bgcolor: '#9C27B0' } }}
                        >
                            {editingTraining ? 'Ажурирај' : 'Додади'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete confirmation */}
                <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, training: null })}>
                    <DialogTitle sx={{ color: '#f44336' }}>
                        ⚠️ Потврди бришење
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Дали сте сигурни дека сакате да го избришете тренингот "{deleteConfirm.training?.name}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteConfirm({ open: false, training: null })}>
                            Откажи
                        </Button>
                        <Button onClick={handleDeleteTraining} color="error" variant="contained">
                            Избриши
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default TrainGym;